import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useTopBannerContext } from './contexts/TopBannerContext';

// Sidebar entries are top-level path segments (e.g. '/sales/', '/settings/'), so a change of
// section id approximates "the user clicked a different sidebar item", unlike list -> detail
// navigation or opening a nested modal route, which stay under the same first segment.
function getSectionId(pathname) {
    return pathname.split('/').filter(Boolean)[0] ?? '';
}

// Delays the auto-reload after a version is detected so navigations right after a deploy don't
// all reload at once and spike the backend.
const AUTO_RELOAD_MIN_DELAY_MS = 3 * 24 * 60 * 60 * 1000;

const CHECK_VERSION_POLL_DELAY = 5 * 60 * 1000;

function useInterval(callback, delay) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

const AutoRefresh = ({ localStorageKey }) => {
    const { t } = useTranslation();
    const { setTopBanner } = useTopBannerContext();
    const { pathname } = useLocation();
    const hasNewVersionRef = useRef(false);
    const versionDetectedAtRef = useRef(null);
    const previousSectionIdRef = useRef(getSectionId(pathname));

    const reloadAfterUpdate = useCallback(
        (reason) => {
            console.info(`${localStorageKey} reloading after new version detected (${reason})`);
            window.location.reload(true);
        },
        [localStorageKey]
    );

    const checkVersion = async () => {
        return fetch(versionUrl, { cache: 'no-store' })
            .then((response) => response.json())
            .then((result) => {
                const remoteHash = result.gitHash;
                const localHash = window.localStorage.getItem(localStorageKey);

                if (!localHash) {
                    window.localStorage.setItem(localStorageKey, remoteHash);
                    console.info(`${localStorageKey} git hash set to "${remoteHash}"`);
                    return;
                }

                if (remoteHash !== localHash) {
                    if (!hasNewVersionRef.current) {
                        versionDetectedAtRef.current = Date.now();
                    }
                    hasNewVersionRef.current = true;

                    setTopBanner({
                        text: t('A new version is available. Please click here to reload your app.'),
                        id: 'please-reload-your-app',
                        onClick: () => {
                            window.localStorage.setItem(localStorageKey, remoteHash);
                            reloadAfterUpdate('manual banner click');
                        },
                    });
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };
    // eslint-disable-next-line no-undef
    const versionUrl = `${__ROUTER_BASE_NAME__}version.json`; // var is replace by ViteJS

    useEffect(() => {
        window.localStorage.removeItem(localStorageKey);
        fetch(versionUrl)
            .then((response) => response.json())
            .then((result) => {
                const remoteHash = result.gitHash;
                window.localStorage.setItem(localStorageKey, remoteHash);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [localStorageKey, versionUrl]);

    useInterval(() => {
        checkVersion();
    }, CHECK_VERSION_POLL_DELAY); // 5 minutes

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkVersion();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- I want to run this effect only once,
    }, []);

    useEffect(() => {
        const sectionId = getSectionId(pathname);
        const hasChangedSection = sectionId !== previousSectionIdRef.current;

        previousSectionIdRef.current = sectionId;

        const hasMinDelayElapsed =
            versionDetectedAtRef.current !== null &&
            Date.now() - versionDetectedAtRef.current >= AUTO_RELOAD_MIN_DELAY_MS;

        if (hasChangedSection && hasNewVersionRef.current && hasMinDelayElapsed) {
            reloadAfterUpdate(`section changed to "${sectionId}"`);
        }
    }, [pathname, reloadAfterUpdate]);

    return null;
};

export { AutoRefresh };
